import React, { useEffect, useState } from "react";
import {
  educationAuth,
  educationDb,
} from "./firebase.js";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import {
  onAuthStateChanged,
  signOut,
} from "firebase/auth";

import {
  doc,
  getDoc,
} from "firebase/firestore";

// =======================
// Pages HDC Éducation
// =======================

import Dashboard from "./pages/Dashboard.js";
import Accueil from "./pages/Accueil.js";
import Cours from "./pages/Cours.js";
import Contact from "./pages/Contact.js";
import Connexion from "./pages/Connexion.js";
import Inscription from "./pages/Inscription.js";
import ConnexionSMS from "./pages/connexionSMS.js";
import AdminSoumissions from "./pages/AdminSoumissions.js";
import Profile from "./pages/Profile.js";

// =======================
// Pages HDC Finance
// =======================

import FinanceHome from "./pages/finance/FinanceHome.js";
import FinanceRequest from "./pages/finance/FinanceRequest.js";
import FinanceRequests from "./pages/finance/FinanceRequests.js";
import FinanceApplicationForm from "./pages/finance/FinanceApplicationForm.js";
import FinanceDocuments from "./pages/finance/FinanceDocuments.js";
import FinanceReview from "./pages/finance/FinanceReview.js";
import FinanceLogin from "./pages/finance/FinanceLogin.js";
import FinanceRegister from "./pages/finance/FinanceRegister.js";
import FinanceAdmin from "./pages/finance/FinanceAdmin.js";

// =======================
// Pages Groupe + Consulting
// =======================

import Groupe from "./pages/Groupe.js";
import ConsultingHome from "./pages/consulting/ConsultingHome.js";
import ConsultingServices from "./pages/consulting/ConsultingServices.js";
import ConsultingAbout from "./pages/consulting/ConsultingAbout.js";
import ConsultingContact from "./pages/consulting/ConsultingContact.js";

// =======================
// Composants
// =======================

import ProtectedRoute from "./components/ProtectedRoute.js";
import FinanceProtectedRoute from "./components/FinanceProtectedRoute.js";
import Chatbot from "./components/Chatbot.js";

// =======================
// Layout simple du Groupe
// =======================

function BareLayout() {
  return <Outlet />;
}

// Footer global sans trait de séparation
function GlobalFooter() {
  return (
    <footer style={styles.globalFooter}>
      © {new Date().getFullYear()} Groupe HDC • Tous droits réservés
    </footer>
  );
}

// =======================
// Layout HDC Consulting + Finance
// =======================

function ConsultingLayout() {
  return (
    <div style={styles.consultingLayout}>
      <main style={styles.consultingMain}>
        <Outlet />
      </main>

      <GlobalFooter />
    </div>
  );
}

// =======================
// Élément de navigation Éducation
// =======================

function NavItem({ to, children, isActive }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      to={to}
      style={{
        ...styles.link,
        ...(isHovered ? styles.linkHover : {}),
        ...(isActive ? styles.activeLink : {}),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </Link>
  );
}

// =======================
// Layout HDC Éducation
// =======================

function EducationLayout({ user, isAdmin, onLogout }) {
  const location = useLocation();

  return (
    <>
      {/* En-tête fixe Éducation */}
      <header style={styles.header}>
        <div style={styles.logo}>
          <img
            src="https://cdn-icons-png.flaticon.com/512/906/906175.png"
            alt="Logo Groupe HDC"
            style={styles.logoImg}
          />

          <h2 style={styles.logoTitle}>
            Groupe HDC
          </h2>
        </div>

        <nav style={styles.nav}>
          <NavItem
            to="/"
            isActive={location.pathname === "/"}
          >
            Retour à l’accueil du Groupe
          </NavItem>

          <NavItem
            to="/contact"
            isActive={location.pathname === "/contact"}
          >
            Contact
          </NavItem>

          {user && isAdmin && (
            <NavItem
              to="/cours"
              isActive={location.pathname === "/cours"}
            >
              Cours
            </NavItem>
          )}

          {user && (
            <NavItem
              to="/profile"
              isActive={location.pathname === "/profile"}
            >
              Mon profil
            </NavItem>
          )}

          {user && isAdmin && (
  <NavItem
    to="/admin"
    isActive={location.pathname.startsWith("/admin")}
  >
    Tableau de bord admin
  </NavItem>
)}

          {!user ? (
            <NavItem
              to="/connexion"
              isActive={
                location.pathname === "/connexion" ||
                location.pathname === "/inscription"
              }
            >
              Connexion / Inscription
            </NavItem>
          ) : (
            <button
              type="button"
              onClick={onLogout}
              style={styles.logoutBtn}
            >
              Se déconnecter
            </button>
          )}
        </nav>
      </header>

      {/* Contenu des pages Éducation */}
      <main style={styles.main}>
        <Outlet />
      </main>

      <GlobalFooter />
      <Chatbot />
    </>
  );
}

// Protège réellement l'administration HDC Éducation.
function EducationAdminRoute({ user, isAdmin, adminLoading, children }) {
  if (adminLoading) {
    return (
      <div style={styles.adminLoading}>
        Vérification de l’accès administrateur…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/connexion" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/education" replace />;
  }

  return children;
}

// =======================
// Application principale
// =======================

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);

  /*
  |--------------------------------------------------------------------------
  | SESSION HDC ÉDUCATION
  |--------------------------------------------------------------------------
  |
  | App.js surveille seulement l'instance educationAuth.
  |
  | Une personne connectée à Consulting ou Finance ne sera donc plus
  | automatiquement considérée comme connectée à HDC Éducation.
  |
  */

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      educationAuth,
      async (currentUser) => {
        setUser(currentUser);

        if (!currentUser) {
          setIsAdmin(false);
          setAdminLoading(false);
          return;
        }

        setAdminLoading(true);

        try {
          const adminSnapshot = await getDoc(
            doc(educationDb, "admins", currentUser.uid)
          );

          setIsAdmin(adminSnapshot.exists());
        } catch (error) {
          console.error(
            "Erreur pendant la vérification du rôle administrateur Éducation :",
            error
          );
          setIsAdmin(false);
        } finally {
          setAdminLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | DÉCONNEXION HDC ÉDUCATION
  |--------------------------------------------------------------------------
  |
  | Cette déconnexion ferme uniquement la session Éducation.
  | Elle ne déconnecte pas l'utilisateur de Finance.
  |
  */

  const handleLogout = async () => {
    try {
      await signOut(educationAuth);
      setUser(null);
      setIsAdmin(false);

      alert("🚪 Déconnecté avec succès !");
    } catch (error) {
      console.error(
        "Erreur pendant la déconnexion Éducation :",
        error
      );
    }
  };

  return (
    <Router>
      <Routes>
        {/* =======================
            ACCUEIL DU GROUPE
        ======================= */}

        <Route element={<BareLayout />}>
          <Route
            path="/"
            element={<Groupe />}
          />

          {/* =======================
              HDC CONSULTING
          ======================= */}

          <Route
            path="/consulting"
            element={<ConsultingLayout />}
          >
            <Route
              index
              element={<ConsultingHome />}
            />

            <Route
              path="services"
              element={<ConsultingServices />}
            />

            <Route
              path="a-propos"
              element={<ConsultingAbout />}
            />

            <Route
              path="contact"
              element={<ConsultingContact />}
            />

            {/* =======================
                HDC FINANCE PUBLIC
            ======================= */}

            <Route
              path="finance"
              element={<FinanceHome />}
            />

            <Route
              path="finance/connexion"
              element={<FinanceLogin />}
            />

            <Route
              path="finance/inscription"
              element={<FinanceRegister />}
            />

            {/* =======================
                HDC FINANCE PROTÉGÉ
            ======================= */}

            <Route
              path="finance/demande"
              element={
                <FinanceProtectedRoute>
                  <FinanceRequest />
                </FinanceProtectedRoute>
              }
            />

            <Route
              path="finance/mes-demandes"
              element={
                <FinanceProtectedRoute>
                  <FinanceRequests />
                </FinanceProtectedRoute>
              }
            />

            <Route
              path="finance/admin"
              element={
                <FinanceProtectedRoute>
                  <FinanceAdmin />
                </FinanceProtectedRoute>
              }
            />

            <Route
              path="finance/formulaire"
              element={
                <FinanceProtectedRoute>
                  <FinanceApplicationForm />
                </FinanceProtectedRoute>
              }
            />

            <Route
              path="finance/documents"
              element={
                <FinanceProtectedRoute>
                  <FinanceDocuments />
                </FinanceProtectedRoute>
              }
            />

            <Route
              path="finance/verification"
              element={
                <FinanceProtectedRoute>
                  <FinanceReview />
                </FinanceProtectedRoute>
              }
            />
          </Route>
        </Route>

        {/* =======================
            HDC ÉDUCATION
        ======================= */}

        <Route
          element={
            <EducationLayout
              user={user}
              isAdmin={isAdmin}
              onLogout={handleLogout}
            />
          }
        >
          {/* Page publique Éducation */}
          <Route
            path="/education"
            element={<Accueil />}
          />

          {/* Authentification Éducation */}
          <Route
            path="/connexion"
            element={<Connexion />}
          />

          <Route
            path="/inscription"
            element={<Inscription />}
          />

          <Route
            path="/connexion-sms"
            element={<ConnexionSMS />}
          />

          <Route
            path="/contact"
            element={<Contact />}
          />

          {/* Pages protégées Éducation */}
          <Route
            path="/cours"
            element={
              <ProtectedRoute>
                <Cours />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <EducationAdminRoute
                user={user}
                isAdmin={isAdmin}
                adminLoading={adminLoading}
              >
                <AdminSoumissions />
              </EducationAdminRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* =======================
            PAGE 404
        ======================= */}

        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>
    </Router>
  );
}

// =======================
// Page 404
// =======================

function NotFound() {
  return (
    <div style={styles.notFoundPage}>
      <div style={styles.notFoundCard}>
        <div style={styles.notFoundCode}>
          404
        </div>

        <h1 style={styles.notFoundTitle}>
          Page introuvable
        </h1>

        <p style={styles.notFoundText}>
          La page demandée n’existe pas ou a été déplacée.
        </p>

        <Link
          to="/"
          style={styles.notFoundButton}
        >
          Retour à l’accueil
        </Link>
      </div>
    </div>
  );
}

// =======================
// Styles
// =======================

const styles = {
  // =======================
  // Layout Consulting + Finance
  // =======================

  consultingLayout: {
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#070b18",
  },

  consultingMain: {
    width: "100%",
    flex: 1,
  },

  // Footer global
  globalFooter: {
    width: "100%",
    boxSizing: "border-box",
    padding: "18px 24px",
    border: "none",
    background: "#070b18",
    color: "#94a3b8",
    fontSize: "12px",
    fontWeight: "600",
    textAlign: "center",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  // =======================
  // En-tête Éducation
  // =======================

  header: {
    width: "100%",
    boxSizing: "border-box",
    padding: "16px 28px",
    background: "#ffffff",
    borderBottom: "1px solid #e2e8f0",
    boxShadow:
      "0 4px 18px rgba(15, 23, 42, 0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "24px",
    flexWrap: "wrap",

    // L’en-tête reste visible pendant le défilement
    position: "sticky",
    top: 0,
    zIndex: 1000,

    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  logo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  logoImg: {
    width: "48px",
    height: "48px",
    objectFit: "contain",
  },

  logoTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "21px",
    fontWeight: "900",
    letterSpacing: "-0.02em",
  },

  nav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    flexWrap: "wrap",
  },

  link: {
    padding: "10px 13px",
    borderRadius: "10px",
    color: "#475569",
    textDecoration: "none",
    fontSize: "12px",
    fontWeight: "800",
    transition:
      "background 0.2s ease, color 0.2s ease, transform 0.2s ease",
  },

  linkHover: {
    background: "#f5f3ff",
    color: "#7c3aed",
    transform: "translateY(-1px)",
  },

  activeLink: {
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    boxShadow:
      "0 8px 18px rgba(37, 99, 235, 0.18)",
  },

  logoutBtn: {
    padding: "10px 14px",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    background: "#fef2f2",
    color: "#dc2626",
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: "800",
    cursor: "pointer",
  },

  // =======================
  // Contenu Éducation
  // =======================

  main: {
    minHeight: "calc(100vh - 143px)",
    background: "#ffffff",
  },

  // =======================
  // Page 404
  // =======================

  notFoundPage: {
    minHeight: "100vh",
    padding: "24px",
    boxSizing: "border-box",
    background:
      "linear-gradient(145deg, #050816, #111936)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  notFoundCard: {
    width: "100%",
    maxWidth: "480px",
    padding: "40px",
    boxSizing: "border-box",
    border:
      "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "24px",
    background:
      "rgba(255, 255, 255, 0.08)",
    color: "#ffffff",
    textAlign: "center",
  },

  notFoundCode: {
    color: "#a78bfa",
    fontSize: "64px",
    fontWeight: "900",
  },

  notFoundTitle: {
    margin: "12px 0 0",
    fontSize: "28px",
    fontWeight: "900",
  },

  notFoundText: {
    margin: "14px 0 26px",
    color: "#cbd5e1",
    fontSize: "13px",
    lineHeight: 1.7,
  },

  notFoundButton: {
    display: "inline-flex",
    padding: "13px 20px",
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    textDecoration: "none",
    fontSize: "12px",
    fontWeight: "900",
  },
};

export default App;
